"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import {
  DollarSign,
  Home,
  Utensils,
  Plane,
  Briefcase,
  PiggyBank,
  ShieldAlert,
  Package,
  Heart,
  Search,
  ChevronDown,
  ChevronUp,
  MapPin,
  Clock,
  RotateCcw,
  Sparkles,
  Info,
  X,
  Copy,
  Pencil,
} from "lucide-react"
import { formatLocation } from "@/lib/utils"

// ── Types ──

type ResidencyData = {
  id: string
  name: string
  slug: string
  location: string
  country: string
  duration: string | null
  costType: string
  applicationFee: string | null
  programCost: string | null
  stipend: string | null
  stipendAmount: string | null
  housing: boolean
  meals: boolean
  studioSpace: boolean
  travelCovered: boolean
  fee: string | null
}

type CostBreakdown = {
  label: string
  amount: number
  category: "direct" | "hidden" | "offset"
  note?: string
  icon: React.ReactNode
}

// ── Helpers ──

function parseDurationWeeks(duration: string | null): number {
  if (!duration) return 4
  // Try "X weeks" or "X-Y weeks" → use midpoint
  const rangeMatch = duration.match(/(\d+)\s*[-–]\s*(\d+)\s*weeks?/i)
  if (rangeMatch) return Math.round((parseInt(rangeMatch[1]) + parseInt(rangeMatch[2])) / 2)
  const singleMatch = duration.match(/(\d+)\s*weeks?/i)
  if (singleMatch) return parseInt(singleMatch[1])
  // Try months
  const monthRange = duration.match(/(\d+)\s*[-–]\s*(\d+)\s*months?/i)
  if (monthRange) return Math.round(((parseInt(monthRange[1]) + parseInt(monthRange[2])) / 2) * 4.3)
  const monthSingle = duration.match(/(\d+)\s*months?/i)
  if (monthSingle) return Math.round(parseInt(monthSingle[1]) * 4.3)
  return 4
}

function parseDollarAmount(str: string | null | undefined): number {
  if (!str) return 0
  // Extract first dollar/number amount: "$35", "$1,000/month", "£2,000", etc.
  const match = str.replace(/,/g, "").match(/[\$£€]?\s*(\d+(?:\.\d+)?)/)
  if (match) return parseFloat(match[1])
  return 0
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

// Average daily meal cost by region (rough USD estimates)
function estimateDailyMealCost(country: string): number {
  const lower = country.toLowerCase()
  if (["united states", "canada", "australia", "united kingdom", "norway", "sweden", "denmark", "switzerland", "iceland", "japan", "singapore"].some(c => lower.includes(c))) return 25
  if (["germany", "france", "netherlands", "belgium", "austria", "italy", "spain", "ireland", "finland", "new zealand", "south korea"].some(c => lower.includes(c))) return 20
  if (["mexico", "brazil", "argentina", "colombia", "chile", "thailand", "vietnam", "indonesia", "india", "philippines", "turkey", "portugal", "greece", "poland", "czech"].some(c => lower.includes(c))) return 12
  return 18 // default
}

// ── Main component ──

export default function TrueCostCalculator() {
  // Residency selection
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<ResidencyData[]>([])
  const [selectedResidency, setSelectedResidency] = useState<ResidencyData | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  // User situation inputs
  const [monthlyRent, setMonthlyRent] = useState("")
  const [rentStrategy, setRentStrategy] = useState<"keep" | "sublet" | "vacationRental">("keep")
  const [subletIncome, setSubletIncome] = useState("")
  const [monthlyIncome, setMonthlyIncome] = useState("")
  const [canWorkRemotely, setCanWorkRemotely] = useState(false)
  const [travelCost, setTravelCost] = useState("")
  const [materialsBudget, setMaterialsBudget] = useState("")
  const [healthInsurance, setHealthInsurance] = useState("")
  const [dependentCare, setDependentCare] = useState("")
  const [shippingCost, setShippingCost] = useState("")
  const [durationWeeks, setDurationWeeks] = useState(4)
  const [customDuration, setCustomDuration] = useState(false)
  const [hasFellowship, setHasFellowship] = useState(false)

  // Manual entry (for residencies not in the directory)
  const [manualMode, setManualMode] = useState(false)
  const [manualName, setManualName] = useState("")
  const [manualCountry, setManualCountry] = useState("")
  const [manualHousing, setManualHousing] = useState(false)
  const [manualMeals, setManualMeals] = useState(false)
  const [manualTravelCovered, setManualTravelCovered] = useState(false)
  const [manualProgramFee, setManualProgramFee] = useState("")
  const [manualAppFee, setManualAppFee] = useState("")
  const [manualStipend, setManualStipend] = useState("")
  const [manualStipendPeriod, setManualStipendPeriod] = useState<"total" | "month" | "week">("total")

  // UI state
  const [showResults, setShowResults] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [copied, setCopied] = useState(false)

  // ── Residency search ──
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.length < 2) {
        setSearchResults([])
        return
      }
      setIsSearching(true)
      try {
        const res = await fetch(`/api/residencies/search?q=${encodeURIComponent(searchQuery)}`)
        if (res.ok) {
          const data = await res.json()
          setSearchResults(data)
          setShowDropdown(true)
        }
      } catch {
        // silently fail
      } finally {
        setIsSearching(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  const selectResidency = useCallback((r: ResidencyData) => {
    setSelectedResidency(r)
    setSearchQuery(r.name)
    setShowDropdown(false)
    if (r.duration && !customDuration) {
      setDurationWeeks(parseDurationWeeks(r.duration))
    }
  }, [customDuration])

  const clearResidency = () => {
    setSelectedResidency(null)
    setSearchQuery("")
    setShowResults(false)
  }

  // Build a residency object from manual inputs (for programs not in the directory)
  const useManualResidency = useCallback(() => {
    const stipendNum = parseFloat(manualStipend) || 0
    const stipendStr = stipendNum > 0
      ? `$${manualStipend}${manualStipendPeriod === "month" ? "/month" : manualStipendPeriod === "week" ? "/week" : ""}`
      : null
    const feeNum = parseFloat(manualProgramFee) || 0
    const costType = feeNum > 0 ? "fee" : stipendNum > 0 ? "stipend" : "free"
    const r: ResidencyData = {
      id: "manual",
      name: manualName.trim() || "Custom residency",
      slug: "",
      location: "",
      country: manualCountry.trim() || "United States",
      duration: null,
      costType,
      applicationFee: manualAppFee ? `$${manualAppFee}` : null,
      programCost: manualProgramFee ? `$${manualProgramFee}` : null,
      stipend: stipendStr,
      stipendAmount: stipendStr,
      housing: manualHousing,
      meals: manualMeals,
      studioSpace: false,
      travelCovered: manualTravelCovered,
      fee: manualProgramFee ? `$${manualProgramFee}` : null,
    }
    setSelectedResidency(r)
    setSearchQuery(r.name)
    setManualMode(false)
  }, [
    manualName, manualCountry, manualHousing, manualMeals, manualTravelCovered,
    manualProgramFee, manualAppFee, manualStipend, manualStipendPeriod,
  ])

  // ── Calculate ──
  const calculate = useCallback((): CostBreakdown[] => {
    const weeks = durationWeeks
    const months = weeks / 4.3
    const costs: CostBreakdown[] = []

    // 1. Application fee
    const appFee = parseDollarAmount(selectedResidency?.applicationFee ?? selectedResidency?.fee)
    if (appFee > 0) {
      costs.push({
        label: "Application fee",
        amount: appFee,
        category: "direct",
        icon: <DollarSign size={14} />,
      })
    }

    // 2. Program fee/cost
    const progCost = parseDollarAmount(selectedResidency?.programCost)
    const programFee = progCost > 0
      ? progCost
      : (selectedResidency?.costType === "fee" || selectedResidency?.costType === "self-funded")
        ? parseDollarAmount(selectedResidency.fee)
        : 0
    if (programFee > 0) {
      if (hasFellowship) {
        costs.push({
          label: "Program fee (waived)",
          amount: 0,
          category: "offset",
          note: `${formatCurrency(programFee)} program fee covered by your fellowship or fee waiver`,
          icon: <PiggyBank size={14} />,
        })
      } else {
        costs.push({
          label: "Program fee",
          amount: programFee,
          category: "direct",
          icon: <DollarSign size={14} />,
        })
      }
    }

    // 3. Rent at home (continues while away)
    const rent = parseFloat(monthlyRent) || 0
    if (rent > 0) {
      const grossRent = Math.round(rent * months)
      const subletOffset = parseFloat(subletIncome) || 0

      if (rentStrategy === "keep") {
        costs.push({
          label: "Rent / mortgage at home",
          amount: grossRent,
          category: "hidden",
          note: `${formatCurrency(rent)}/mo × ${months.toFixed(1)} months — your lease doesn't pause`,
          icon: <Home size={14} />,
        })
      } else if (rentStrategy === "sublet" && subletOffset > 0) {
        const subletTotal = Math.round(subletOffset * months)
        const netRent = grossRent - subletTotal
        costs.push({
          label: "Rent at home (after sublet)",
          amount: Math.max(0, netRent),
          category: netRent > 0 ? "hidden" : "offset",
          note: `${formatCurrency(rent)}/mo rent − ${formatCurrency(subletOffset)}/mo sublet income`,
          icon: <Home size={14} />,
        })
      } else if (rentStrategy === "vacationRental" && subletOffset > 0) {
        const rentalTotal = Math.round(subletOffset * months)
        const netRent = grossRent - rentalTotal
        costs.push({
          label: "Rent at home (after short-term rental)",
          amount: Math.max(0, netRent),
          category: netRent > 0 ? "hidden" : "offset",
          note: `${formatCurrency(rent)}/mo rent − ${formatCurrency(subletOffset)}/mo rental income (check local laws)`,
          icon: <Home size={14} />,
        })
      } else {
        // sublet/vacation but no income entered — show full rent
        costs.push({
          label: "Rent / mortgage at home",
          amount: grossRent,
          category: "hidden",
          note: `${formatCurrency(rent)}/mo × ${months.toFixed(1)} months`,
          icon: <Home size={14} />,
        })
      }
    }

    // 4. Lost income
    const income = parseFloat(monthlyIncome) || 0
    if (income > 0 && !canWorkRemotely) {
      costs.push({
        label: "Lost income",
        amount: Math.round(income * months),
        category: "hidden",
        note: `${formatCurrency(income)}/mo × ${months.toFixed(1)} months — the biggest hidden cost`,
        icon: <Briefcase size={14} />,
      })
    }

    // 5. Travel
    const travel = parseFloat(travelCost) || 0
    if (travel > 0 && !selectedResidency?.travelCovered) {
      costs.push({
        label: "Travel (round trip)",
        amount: travel,
        category: "direct",
        icon: <Plane size={14} />,
      })
    } else if (selectedResidency?.travelCovered && travel > 0) {
      costs.push({
        label: "Travel (covered by program)",
        amount: 0,
        category: "offset",
        note: `${formatCurrency(travel)} covered`,
        icon: <Plane size={14} />,
      })
    }

    // 6. Meals (if not provided)
    if (selectedResidency && !selectedResidency.meals) {
      const dailyCost = estimateDailyMealCost(selectedResidency.country)
      const mealTotal = Math.round(dailyCost * weeks * 7)
      costs.push({
        label: "Meals",
        amount: mealTotal,
        category: "hidden",
        note: `~${formatCurrency(dailyCost)}/day × ${weeks} weeks — meals not included`,
        icon: <Utensils size={14} />,
      })
    }

    // 7. Materials
    const materials = parseFloat(materialsBudget) || 0
    if (materials > 0) {
      costs.push({
        label: "Materials & supplies",
        amount: materials,
        category: "direct",
        icon: <Package size={14} />,
      })
    }

    // 8. Health insurance
    const insurance = parseFloat(healthInsurance) || 0
    if (insurance > 0) {
      costs.push({
        label: "Health insurance",
        amount: Math.round(insurance * months),
        category: "hidden",
        note: `${formatCurrency(insurance)}/mo — especially for international residencies`,
        icon: <ShieldAlert size={14} />,
      })
    }

    // 9. Dependent/pet care
    const depCare = parseFloat(dependentCare) || 0
    if (depCare > 0) {
      costs.push({
        label: "Dependent / pet care",
        amount: Math.round(depCare * months),
        category: "hidden",
        note: `${formatCurrency(depCare)}/mo while you're away`,
        icon: <Heart size={14} />,
      })
    }

    // 10. Shipping artwork home
    const shipping = parseFloat(shippingCost) || 0
    if (shipping > 0) {
      costs.push({
        label: "Ship artwork home",
        amount: shipping,
        category: "direct",
        icon: <Package size={14} />,
      })
    }

    // 11. Stipend offset
    const stipend = parseDollarAmount(selectedResidency?.stipendAmount ?? selectedResidency?.stipend)
    if (stipend > 0) {
      // Detect the stipend period so per-week/per-day rates aren't undercounted
      const stipendStr = (selectedResidency?.stipendAmount ?? selectedResidency?.stipend ?? "").toLowerCase()
      let totalStipend = stipend
      let note = "Total stipend provided"
      if (/(\/\s*mo|\/month|per month|monthly)/.test(stipendStr)) {
        totalStipend = Math.round(stipend * months)
        note = `${formatCurrency(stipend)}/mo × ${months.toFixed(1)} months`
      } else if (/(\/\s*wk|\/week|per week|weekly)/.test(stipendStr)) {
        totalStipend = Math.round(stipend * weeks)
        note = `${formatCurrency(stipend)}/wk × ${weeks} weeks`
      } else if (/(\/\s*day|per day|daily|per diem)/.test(stipendStr)) {
        totalStipend = Math.round(stipend * weeks * 7)
        note = `${formatCurrency(stipend)}/day × ${weeks * 7} days`
      }
      costs.push({
        label: "Stipend",
        amount: -totalStipend,
        category: "offset",
        note,
        icon: <PiggyBank size={14} />,
      })
    }

    return costs
  }, [
    selectedResidency, durationWeeks, monthlyRent, rentStrategy, subletIncome,
    monthlyIncome, canWorkRemotely, travelCost, materialsBudget, healthInsurance,
    dependentCare, shippingCost, hasFellowship,
  ])

  const handleCalculate = () => setShowResults(true)

  const reset = () => {
    setSelectedResidency(null)
    setSearchQuery("")
    setMonthlyRent("")
    setRentStrategy("keep")
    setSubletIncome("")
    setMonthlyIncome("")
    setCanWorkRemotely(false)
    setTravelCost("")
    setMaterialsBudget("")
    setHealthInsurance("")
    setDependentCare("")
    setShippingCost("")
    setDurationWeeks(4)
    setCustomDuration(false)
    setHasFellowship(false)
    setManualMode(false)
    setManualName("")
    setManualCountry("")
    setManualHousing(false)
    setManualMeals(false)
    setManualTravelCovered(false)
    setManualProgramFee("")
    setManualAppFee("")
    setManualStipend("")
    setManualStipendPeriod("total")
    setShowResults(false)
    setShowAdvanced(false)
  }

  const costs = showResults ? calculate() : []
  const directCosts = costs.filter(c => c.category === "direct")
  const hiddenCosts = costs.filter(c => c.category === "hidden")
  const offsets = costs.filter(c => c.category === "offset")
  const totalDirect = directCosts.reduce((s, c) => s + c.amount, 0)
  const totalHidden = hiddenCosts.reduce((s, c) => s + c.amount, 0)
  const totalOffsets = offsets.reduce((s, c) => s + c.amount, 0)
  const grandTotal = totalDirect + totalHidden + totalOffsets
  const emergencyBuffer = Math.round(Math.max(0, grandTotal) * 0.1)
  const barTotal = totalDirect + totalHidden // guard against divide-by-zero in the breakdown bar

  // Does the selected program carry a fee an artist might get waived?
  const programFeeAmount = selectedResidency
    ? (parseDollarAmount(selectedResidency.programCost) > 0
        ? parseDollarAmount(selectedResidency.programCost)
        : (selectedResidency.costType === "fee" || selectedResidency.costType === "self-funded")
          ? parseDollarAmount(selectedResidency.fee)
          : 0)
    : 0

  const isManual = selectedResidency?.id === "manual"

  const copySummary = useCallback(async () => {
    if (!selectedResidency) return
    const lines = [
      `True Cost estimate — ${selectedResidency.name}`,
      `${formatLocation(selectedResidency.location, selectedResidency.country)} · ${durationWeeks} weeks`,
      "",
      ...costs.map((c) => `  ${c.label}: ${c.amount < 0 ? "−" : ""}${formatCurrency(Math.abs(c.amount))}`),
      "",
      `Estimated true cost: ${formatCurrency(Math.max(0, grandTotal))}`,
      `Recommended emergency buffer (10%): ${formatCurrency(emergencyBuffer)}`,
      "",
      `Estimate via Rate My Artist Residency — ratemyartistresidency.com`,
    ]
    try {
      await navigator.clipboard.writeText(lines.join("\n"))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard unavailable — no-op
    }
  }, [selectedResidency, costs, durationWeeks, grandTotal, emergencyBuffer])

  // ── Render ──

  if (showResults && selectedResidency) {
    return (
      <div className="animate-fade-in">
        {/* Header */}
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-emerald-100/80 via-teal-50/60 to-transparent p-8 sm:p-10 mb-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(16,185,129,0.08),transparent_70%)]" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign size={16} className="text-emerald-600" />
              <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
                True Cost Breakdown
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold leading-tight mb-1 text-[var(--foreground)]">
              {selectedResidency.name}
            </h2>
            <p className="text-sm text-[var(--muted-foreground)]">
              {formatLocation(selectedResidency.location, selectedResidency.country)} &middot; {durationWeeks} weeks
            </p>
          </div>
        </div>

        {/* Grand total */}
        <div className="border-2 border-[var(--foreground)] rounded-xl p-6 mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-[var(--muted-foreground)] mb-1">
            Estimated true cost
          </p>
          <p className="text-4xl sm:text-5xl font-bold tabular-nums text-[var(--foreground)]">
            {formatCurrency(Math.max(0, grandTotal))}
          </p>
          {selectedResidency.costType === "free" && grandTotal > 500 && (
            <p className="text-sm text-[var(--muted-foreground)] mt-2 italic">
              This residency is listed as &ldquo;free&rdquo; — but free doesn&rsquo;t mean no cost.
            </p>
          )}
          {emergencyBuffer > 0 && (
            <p className="text-xs text-[var(--muted-foreground)] mt-2">
              + {formatCurrency(emergencyBuffer)} recommended emergency buffer (10%)
            </p>
          )}
        </div>

        {/* Cost breakdown sections */}
        {directCosts.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-[var(--primary)] mb-3">
              Direct Costs
            </h3>
            <div className="space-y-2">
              {directCosts.map((c, i) => (
                <CostRow key={i} item={c} />
              ))}
            </div>
            <div className="flex justify-between mt-3 pt-2 border-t border-[var(--border)] text-sm font-semibold">
              <span>Subtotal</span>
              <span className="tabular-nums">{formatCurrency(totalDirect)}</span>
            </div>
          </div>
        )}

        {hiddenCosts.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-orange-600 mb-3">
              Hidden Costs
            </h3>
            <p className="text-xs text-[var(--muted-foreground)] mb-3">
              These are the costs artists consistently underestimate by 30&ndash;40%.
            </p>
            <div className="space-y-2">
              {hiddenCosts.map((c, i) => (
                <CostRow key={i} item={c} />
              ))}
            </div>
            <div className="flex justify-between mt-3 pt-2 border-t border-[var(--border)] text-sm font-semibold text-orange-600">
              <span>Subtotal</span>
              <span className="tabular-nums">{formatCurrency(totalHidden)}</span>
            </div>
          </div>
        )}

        {offsets.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-emerald-600 mb-3">
              Offsets & Support
            </h3>
            <div className="space-y-2">
              {offsets.map((c, i) => (
                <CostRow key={i} item={c} />
              ))}
            </div>
            <div className="flex justify-between mt-3 pt-2 border-t border-[var(--border)] text-sm font-semibold text-emerald-600">
              <span>Subtotal</span>
              <span className="tabular-nums">{formatCurrency(totalOffsets)}</span>
            </div>
          </div>
        )}

        {/* Visual bar breakdown */}
        <div className="mb-8">
          <h3 className="text-sm font-semibold mb-3">Where the money goes</h3>
          <div className="h-6 rounded-full overflow-hidden flex bg-[var(--muted)]">
            {barTotal > 0 && totalDirect > 0 && (
              <div
                className="bg-[var(--foreground)] transition-all duration-700"
                style={{ width: `${(totalDirect / barTotal) * 100}%` }}
                title={`Direct: ${formatCurrency(totalDirect)}`}
              />
            )}
            {barTotal > 0 && totalHidden > 0 && (
              <div
                className="bg-orange-400 transition-all duration-700"
                style={{ width: `${(totalHidden / barTotal) * 100}%` }}
                title={`Hidden: ${formatCurrency(totalHidden)}`}
              />
            )}
          </div>
          <div className="flex gap-4 mt-2 text-xs text-[var(--muted-foreground)]">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[var(--foreground)]" />
              Direct costs
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-orange-400" />
              Hidden costs
            </span>
            {Math.abs(totalOffsets) > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                Stipend/offsets
              </span>
            )}
          </div>
        </div>

        {/* Data context callout */}
        <div className="bg-[var(--secondary)] rounded-xl p-5 mb-8">
          <div className="flex items-start gap-2">
            <Info size={14} className="text-[var(--muted-foreground)] mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-[var(--foreground)] mb-1">
                Why this matters
              </p>
              <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">
                Research shows artists underestimate residency costs by 30&ndash;40%. One artist spent $9,000 on a
                &ldquo;free&rdquo; residency in France when accounting for rent at home, travel, materials, and lost
                freelance income. W.A.G.E. notes that &ldquo;most artists can&rsquo;t afford to forfeit the little
                pay they might already have.&rdquo; Knowing the true cost upfront lets you plan — or negotiate.
              </p>
            </div>
          </div>
        </div>

        {/* Resource links */}
        <div className="bg-[var(--secondary)] rounded-xl p-5 mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-[var(--primary)] mb-3">
            Next steps
          </p>
          <ul className="space-y-2 text-sm text-[var(--muted-foreground)]">
            <li>
              <a href="/resources/funding-guide" className="text-[var(--primary)] hover:underline">How to Fund Your Residency</a>
              {" "}&mdash; grants, fiscal sponsors, and creative budgeting
            </li>
            <li>
              <a href="/resources/opportunity-quiz" className="text-[var(--primary)] hover:underline">Should You Take This Opportunity?</a>
              {" "}&mdash; the decision quiz, now that you know the cost
            </li>
            {!isManual && (
              <li>
                <a href={`/residency/${selectedResidency.slug}`} className="text-[var(--primary)] hover:underline">
                  View {selectedResidency.name} on RMAR
                </a>
                {" "}&mdash; reviews, details, and open calls
              </li>
            )}
          </ul>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => setShowResults(false)}
            className="inline-flex items-center gap-2 text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
          >
            <ChevronUp size={14} />
            Edit inputs
          </button>
          <button
            onClick={copySummary}
            className="inline-flex items-center gap-2 text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
          >
            <Copy size={14} />
            {copied ? "Copied!" : "Copy summary"}
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
          >
            <RotateCcw size={14} />
            Start over
          </button>
        </div>

        {/* Disclaimer */}
        <p className="mt-8 text-xs text-[var(--muted-foreground)] italic">
          These are estimates based on the information you provided and publicly available residency data.
          Actual costs may vary. This tool is for planning purposes — not financial advice.
        </p>

        <style jsx>{`
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: fade-in 0.5s ease-out;
          }
        `}</style>
      </div>
    )
  }

  // ── Input form ──
  return (
    <div className="animate-fade-in">
      {/* Intro header */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-emerald-100/80 via-teal-50/60 to-transparent p-8 sm:p-12 mb-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(16,185,129,0.08),transparent_70%)]" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign size={16} className="text-emerald-600" />
            <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
              Financial Planning Tool
            </span>
          </div>
          <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl font-semibold italic leading-tight mb-4 text-[var(--foreground)]">
            What Will This<br />Residency Actually Cost?
          </h2>
          <p className="text-base sm:text-lg text-[var(--muted-foreground)] max-w-xl leading-relaxed">
            Even &ldquo;free&rdquo; residencies have a price. Select a program, tell us your situation,
            and we&rsquo;ll show you the real number — including the costs nobody talks about.
          </p>
        </div>
      </div>

      {/* Context block */}
      <div className="bg-[var(--secondary)] rounded-xl p-5 mb-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-[var(--primary)] mb-2">
          Why artists need this
        </p>
        <p className="text-sm text-[var(--muted-foreground)] leading-relaxed">
          Artists underestimate residency costs by 30&ndash;40% (Fractured Atlas). One artist documented
          spending $9,000 on a &ldquo;free&rdquo; residency in France. Lost income, rent at home, travel,
          meals, materials, insurance, pet care — it adds up. This calculator surfaces everything so
          you can plan honestly.
        </p>
      </div>

      {/* Step 1: Select residency */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-1">1. Select a residency</h3>
        <p className="text-xs text-[var(--muted-foreground)] mb-4">
          Search our directory or enter details manually.
        </p>

        <div ref={searchRef} className="relative">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                if (selectedResidency) setSelectedResidency(null)
              }}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              placeholder="Search residencies — e.g. MacDowell, Yaddo, VCCA..."
              className="w-full pl-10 pr-10 py-3 rounded-xl border border-[var(--border)] bg-[var(--background)] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all"
            />
            {selectedResidency && (
              <button
                onClick={clearResidency}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {showDropdown && searchResults.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-[var(--background)] border border-[var(--border)] rounded-xl shadow-lg max-h-64 overflow-y-auto">
              {searchResults.map((r) => (
                <button
                  key={r.id}
                  onClick={() => selectResidency(r)}
                  className="w-full text-left px-4 py-3 hover:bg-[var(--secondary)] transition-colors border-b border-[var(--border)] last:border-b-0"
                >
                  <span className="text-sm font-medium text-[var(--foreground)]">{r.name}</span>
                  <span className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-[var(--muted-foreground)]">
                      <MapPin size={10} className="inline mr-0.5" />
                      {formatLocation(r.location, r.country)}
                    </span>
                    {r.duration && (
                      <span className="text-xs text-[var(--muted-foreground)]">
                        <Clock size={10} className="inline mr-0.5" />
                        {r.duration}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}

          {isSearching && (
            <p className="text-xs text-[var(--muted-foreground)] mt-2">Searching...</p>
          )}
        </div>

        {/* Manual entry — for residencies not in the directory */}
        {!selectedResidency && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setManualMode((m) => !m)}
              className="inline-flex items-center gap-1.5 text-sm text-[var(--primary)] hover:underline"
            >
              <Pencil size={13} />
              {manualMode ? "Cancel manual entry" : "Can't find it? Enter a residency manually"}
            </button>

            {manualMode && (
              <div className="mt-3 p-4 rounded-xl border border-[var(--border)] bg-[var(--secondary)] space-y-4 animate-fade-in">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Program name</label>
                    <input
                      type="text"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      placeholder="e.g. Coastal Studio Residency"
                      className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--background)] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Country</label>
                    <input
                      type="text"
                      value={manualCountry}
                      onChange={(e) => setManualCountry(e.target.value)}
                      placeholder="e.g. Italy"
                      className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--background)] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    />
                    <p className="text-[11px] text-[var(--muted-foreground)] mt-1">Used to estimate meal costs</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  {([
                    { label: "Housing included", value: manualHousing, set: setManualHousing },
                    { label: "Meals included", value: manualMeals, set: setManualMeals },
                    { label: "Travel covered", value: manualTravelCovered, set: setManualTravelCovered },
                  ]).map(({ label, value, set }) => (
                    <label key={label} className="flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => set(e.target.checked)}
                        className="rounded border-[var(--border)] text-emerald-600 focus:ring-emerald-200"
                      />
                      {label}
                    </label>
                  ))}
                </div>

                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Program fee ($)</label>
                    <input type="number" inputMode="numeric" value={manualProgramFee} onChange={(e) => setManualProgramFee(e.target.value)} placeholder="0" className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--background)] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Application fee ($)</label>
                    <input type="number" inputMode="numeric" value={manualAppFee} onChange={(e) => setManualAppFee(e.target.value)} placeholder="0" className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--background)] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Stipend ($)</label>
                    <div className="flex gap-2">
                      <input type="number" inputMode="numeric" value={manualStipend} onChange={(e) => setManualStipend(e.target.value)} placeholder="0" className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--background)] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                      <select value={manualStipendPeriod} onChange={(e) => setManualStipendPeriod(e.target.value as "total" | "month" | "week")} className="px-2 py-2 rounded-lg border border-[var(--border)] bg-[var(--background)] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200">
                        <option value="total">total</option>
                        <option value="month">/mo</option>
                        <option value="week">/wk</option>
                      </select>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={useManualResidency}
                  disabled={!manualName.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--foreground)] text-[var(--background)] text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Use these details
                </button>
              </div>
            )}
          </div>
        )}

        {/* Selected residency info card */}
        {selectedResidency && (
          <div className="mt-4 p-4 rounded-xl border border-emerald-200 bg-emerald-50/50">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold text-sm">{selectedResidency.name}</p>
                <p className="text-xs text-[var(--muted-foreground)]">
                  {formatLocation(selectedResidency.location, selectedResidency.country)}
                  {isManual && " · entered manually"}
                </p>
              </div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                selectedResidency.costType === "free" || selectedResidency.costType === "stipend"
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-orange-100 text-orange-700"
              }`}>
                {selectedResidency.costType === "free" ? "Free" :
                 selectedResidency.costType === "stipend" ? "Paid Stipend" :
                 selectedResidency.costType === "fee" ? "Program Fee" : "Self-Funded"}
              </span>
            </div>
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-[var(--muted-foreground)]">
              {selectedResidency.duration && (
                <span><Clock size={11} className="inline mr-1" />{selectedResidency.duration}</span>
              )}
              <span>{selectedResidency.housing ? "Housing included" : "No housing"}</span>
              <span>{selectedResidency.meals ? "Meals included" : "No meals"}</span>
              {selectedResidency.studioSpace && <span>Studio provided</span>}
              {selectedResidency.travelCovered && <span className="text-emerald-600">Travel covered</span>}
              {selectedResidency.stipend && (
                <span className="text-emerald-600">Stipend: {selectedResidency.stipend}</span>
              )}
              {selectedResidency.applicationFee && (
                <span>App fee: {selectedResidency.applicationFee}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Step 2: Your situation */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-1">2. Your situation</h3>
        <p className="text-xs text-[var(--muted-foreground)] mb-4">
          All fields are optional — fill in what applies to you. More detail = more accurate estimate.
        </p>

        <div className="space-y-4">
          {/* Fellowship / fee waiver — only relevant when the program carries a fee */}
          {programFeeAmount > 0 && (
            <label className="flex items-start gap-2 p-3 rounded-lg border border-emerald-200 bg-emerald-50/50 cursor-pointer">
              <input
                type="checkbox"
                checked={hasFellowship}
                onChange={(e) => setHasFellowship(e.target.checked)}
                className="mt-0.5 rounded border-[var(--border)] text-emerald-600 focus:ring-emerald-200"
              />
              <span className="text-sm text-[var(--foreground)]">
                I received a fellowship or fee waiver
                <span className="block text-xs text-[var(--muted-foreground)]">
                  Removes the {formatCurrency(programFeeAmount)} program fee from your estimate. Many programs offer need-based waivers — always ask.
                </span>
              </span>
            </label>
          )}

          {/* Duration */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-1.5">
              <Clock size={14} className="text-[var(--muted-foreground)]" />
              Duration (weeks)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={1}
                max={52}
                value={durationWeeks}
                onChange={(e) => {
                  setDurationWeeks(parseInt(e.target.value) || 1)
                  setCustomDuration(true)
                }}
                className="w-24 px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--background)] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
              {selectedResidency?.duration && (
                <span className="text-xs text-[var(--muted-foreground)]">
                  Program lists: {selectedResidency.duration}
                </span>
              )}
            </div>
          </div>

          {/* Monthly rent */}
          <div>
            <InputField
              label="Monthly rent / mortgage"
              icon={<Home size={14} />}
              value={monthlyRent}
              onChange={setMonthlyRent}
              placeholder="1,200"
              hint="Your rent or mortgage continues while you're away"
            />
            {monthlyRent && parseFloat(monthlyRent) > 0 && (
              <div className="mt-3 ml-1 space-y-2">
                <p className="text-xs font-medium text-[var(--foreground)]">
                  While you&rsquo;re away, will you...
                </p>
                <div className="flex flex-wrap gap-2">
                  {([
                    { value: "keep" as const, label: "Keep paying" },
                    { value: "sublet" as const, label: "Sublet" },
                    { value: "vacationRental" as const, label: "Short-term rental" },
                  ]).map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setRentStrategy(opt.value)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                        rentStrategy === opt.value
                          ? "border-emerald-400 bg-emerald-50 text-emerald-700 font-medium"
                          : "border-[var(--border)] text-[var(--muted-foreground)] hover:border-emerald-200"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {(rentStrategy === "sublet" || rentStrategy === "vacationRental") && (
                  <div className="mt-2">
                    <InputField
                      label={rentStrategy === "sublet" ? "Expected sublet income" : "Expected rental income"}
                      icon={<PiggyBank size={14} />}
                      value={subletIncome}
                      onChange={setSubletIncome}
                      placeholder={rentStrategy === "sublet" ? "900" : "1,500"}
                      hint={rentStrategy === "sublet"
                        ? "Monthly income from subletting — check your lease terms"
                        : "Monthly income from Airbnb, Vrbo, etc. — check local regulations"
                      }
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Monthly income */}
          <div>
            <InputField
              label="Monthly income"
              icon={<Briefcase size={14} />}
              value={monthlyIncome}
              onChange={setMonthlyIncome}
              placeholder="3,000"
              hint="Income you'd lose while at the residency"
            />
            <label className="flex items-center gap-2 mt-2 ml-1">
              <input
                type="checkbox"
                checked={canWorkRemotely}
                onChange={(e) => setCanWorkRemotely(e.target.checked)}
                className="rounded border-[var(--border)] text-emerald-600 focus:ring-emerald-200"
              />
              <span className="text-xs text-[var(--muted-foreground)]">
                I can work remotely during the residency
              </span>
            </label>
          </div>

          {/* Travel */}
          <InputField
            label="Travel cost (round trip)"
            icon={<Plane size={14} />}
            value={travelCost}
            onChange={setTravelCost}
            placeholder="400"
            hint="Check Google Flights or Rome2Rio for estimates"
          />

          {/* Materials */}
          <InputField
            label="Materials & supplies"
            icon={<Package size={14} />}
            value={materialsBudget}
            onChange={setMaterialsBudget}
            placeholder="200"
            hint="Art supplies, equipment rental, printing, etc."
          />

          {/* Advanced fields */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="inline-flex items-center gap-1.5 text-sm text-[var(--primary)] hover:underline"
          >
            {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showAdvanced ? "Fewer fields" : "More fields"} (insurance, care, shipping)
          </button>

          {showAdvanced && (
            <div className="space-y-4 animate-fade-in">
              <InputField
                label="Health / travel insurance"
                icon={<ShieldAlert size={14} />}
                value={healthInsurance}
                onChange={setHealthInsurance}
                placeholder="150"
                hint="Monthly premium — especially important for international residencies"
              />
              <InputField
                label="Dependent / pet care"
                icon={<Heart size={14} />}
                value={dependentCare}
                onChange={setDependentCare}
                placeholder="300"
                hint="Monthly cost for childcare, pet sitter, plant care, etc."
              />
              <InputField
                label="Ship artwork home"
                icon={<Package size={14} />}
                value={shippingCost}
                onChange={setShippingCost}
                placeholder="200"
                hint="Crating and freight — can be $50 domestic to $1,000+ international"
              />
            </div>
          )}
        </div>
      </div>

      {/* Calculate button */}
      <button
        onClick={handleCalculate}
        disabled={!selectedResidency}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--foreground)] text-[var(--background)] text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Sparkles size={16} />
        Calculate true cost
      </button>

      {!selectedResidency && (
        <p className="text-xs text-[var(--muted-foreground)] mt-2">
          Select a residency above to calculate.
        </p>
      )}

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  )
}

// ── Subcomponents ──

function InputField({
  label,
  icon,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string
  icon: React.ReactNode
  value: string
  onChange: (v: string) => void
  placeholder: string
  hint: string
}) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium mb-1.5">
        <span className="text-[var(--muted-foreground)]">{icon}</span>
        {label}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] text-sm">$</span>
        <input
          type="number"
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-7 pr-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--background)] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all"
        />
      </div>
      <p className="text-xs text-[var(--muted-foreground)] mt-1">{hint}</p>
    </div>
  )
}

function CostRow({ item }: { item: CostBreakdown }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-[var(--secondary)]">
      <span className={`mt-0.5 ${
        item.category === "offset" ? "text-emerald-500" :
        item.category === "hidden" ? "text-orange-500" :
        "text-[var(--muted-foreground)]"
      }`}>
        {item.icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium text-[var(--foreground)]">{item.label}</span>
          <span className={`text-sm font-semibold tabular-nums shrink-0 ${
            item.category === "offset" ? "text-emerald-600" :
            item.category === "hidden" ? "text-orange-600" :
            "text-[var(--foreground)]"
          }`}>
            {item.amount < 0 ? `−${formatCurrency(Math.abs(item.amount))}` : formatCurrency(item.amount)}
          </span>
        </div>
        {item.note && (
          <p className="text-xs text-[var(--muted-foreground)] mt-0.5">{item.note}</p>
        )}
      </div>
    </div>
  )
}
